import React from 'react';
import { getChain } from '@/lib/chains';

/**
 * Registry-driven chain badge. Single source of truth = `src/lib/chains.ts`.
 *
 * Styling is DERIVED from the chain's brand `color` (hex) — never hardcoded
 * per-chain. We tint a small pill: a colored dot + the chain `label`, with a
 * `color-mix`-based background/border so any new chain in the registry renders
 * correctly with zero changes here.
 *
 * Returns `null` for unknown/blank chain ids so callers can drop it in safely.
 */
export function ChainBadge({
  chainId,
  className = '',
}: {
  chainId: string;
  className?: string;
}) {
  const chain = getChain(chainId);
  if (!chain) return null;

  const { color, label } = chain;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-mono font-medium border-gray-200 dark:border-neutral-700 ${className}`}
      style={{
        // Subtle brand tint derived from the chain color; readable in both modes.
        backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
        color,
      }}
      title={label}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * Row of `ChainBadge`s for cross-chain entities. Renders `null` when there are
 * no chains. Each entry is resolved through the registry, so unknown ids are
 * silently skipped (their `ChainBadge` returns null).
 */
export function ChainBadges({
  chains,
  className = '',
}: {
  chains?: string[];
  className?: string;
}) {
  if (!chains || chains.length === 0) return null;

  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {chains.map((chainId) => (
        <ChainBadge key={chainId} chainId={chainId} />
      ))}
    </span>
  );
}

export default ChainBadge;
